import {
  SquaresFour, Folder, ListChecks, Truck, Bell, ClipboardText, ChartBar, Heartbeat, Bookmark, ChatCircle, ShieldCheck,
  Lightning, Trophy, CalendarBlank, Robot, Briefcase, PenNib, ArrowsLeftRight, Buildings, Receipt, FileText, Book,
  EnvelopeSimple, UsersThree, TrendUp, Sparkle, ChartLineUp, Compass, TreeStructure, Megaphone, HandPalm, Gauge, Star,
  MapPin, Users, Clock, MapTrifold, WhatsappLogo, Monitor, CalendarCheck, HardHat, Siren, SealCheck, ChalkboardTeacher,
  WarningDiamond, Certificate, Books, GraduationCap, FirstAid, Wrench, Lifebuoy, ChatsCircle, Gear, ChartPieSlice, Coins,
  IdentificationBadge, Flask,
} from "@phosphor-icons/react";

const ADMIN = ["super_admin", "admin", "project_manager"];
const STAFF = ["super_admin", "admin", "project_manager", "team_member", "vendor", "stakeholder"];

/**
 * Staff sidebar, in the order the business reads it: a run of top-level links, then collapsible groups.
 * `label` + `collapsible: true` = a group (needs an `icon` for the icon-only sidebar); no label = loose links.
 * A link may carry `children` (rendered indented beneath it) and `activeWhen(location)` to override highlighting.
 */
export const STAFF_SECTIONS = [
  {
    id: "main",
    items: [
      { to: "/", label: "Dashboard", icon: SquaresFour, roles: STAFF },
      { to: "/supplier-portal", label: "My Portal", icon: Briefcase, roles: ["vendor"] },
      { to: "/job-requests", label: "Job Requests", icon: ClipboardText, roles: ADMIN },
      { to: "/jobs", label: "Jobs", icon: Briefcase, roles: [...ADMIN, "vendor"] },
      { to: "/emergency-jobs", label: "Emergency Jobs", icon: Lightning, roles: [...ADMIN, "vendor"] },
      { to: "/projects", label: "Projects", icon: Folder, roles: [...ADMIN, "team_member", "stakeholder"],
        children: [{ to: "/timeline", label: "Timeline", icon: CalendarBlank, roles: ADMIN }] },
      { to: "/contracts", label: "Contracts", icon: CalendarCheck, roles: ADMIN },
      { to: "/quotes", label: "Quotes", icon: Receipt, roles: ADMIN },
      { to: "/work-orders", label: "Works Orders", icon: ClipboardText, roles: [...ADMIN, "vendor"] },
      { to: "/invoices", label: "Invoices", icon: Receipt, roles: ADMIN },
    ],
  },
  {
    id: "calendar", label: "Calendar", icon: CalendarBlank, collapsible: true, roles: ADMIN,
    items: [
      { to: "/calendar", label: "Schedule", icon: CalendarBlank, roles: ADMIN },
      { to: "/schedule", label: "Team Schedule", icon: UsersThree, roles: ADMIN },
    ],
  },
  {
    id: "communications", label: "Communications", icon: ChatsCircle, collapsible: true,
    items: [
      { to: "/inbox", label: "Inbox", icon: EnvelopeSimple, roles: ADMIN, badge: "inbox" },
      { to: "/whatsapp", label: "WhatsApp", icon: WhatsappLogo, roles: [...ADMIN, "team_member"] },
      { to: "/notifications", label: "Notifications", icon: Bell, roles: STAFF, badge: "notifications" },
    ],
  },
  {
    id: "files",
    items: [{ to: "/files", label: "File Hub", icon: Folder, roles: ADMIN }],
  },
  {
    id: "clients", label: "Clients", icon: Buildings, collapsible: true, roles: ADMIN,
    items: [
      { to: "/clients", label: "Clients", icon: Buildings, roles: ADMIN,
        activeWhen: (loc) => loc.pathname.startsWith("/clients") && new URLSearchParams(loc.search).get("kind") !== "sub" },
      { to: "/clients?kind=sub", label: "Sub Clients", icon: TreeStructure, roles: ADMIN },
      { to: "/properties", label: "Properties", icon: MapPin, roles: ADMIN },
      { to: "/tenant-report-forms", label: "Tenant Report Forms", icon: ClipboardText, roles: ADMIN },
    ],
  },
  {
    id: "in_house_admin", label: "In-House Team", icon: Users, collapsible: true, roles: ADMIN,
    items: [
      { to: "/settings/in-house-team", label: "Team", icon: Users, roles: ADMIN },
      { to: "/dispatch-board", label: "Dispatch Board", icon: CalendarBlank, roles: ADMIN },
      { to: "/live-team-map", label: "Live Team Map", icon: MapTrifold, roles: ADMIN },
      { to: "/in-house/tracking", label: "Tracking", icon: Compass, roles: ADMIN },
      { to: "/in-house/mileage-log", label: "Mileage Log", icon: Gauge, roles: ADMIN },
      { to: "/settings/timesheets", label: "Timesheets", icon: Clock, roles: ADMIN },
      { to: "/settings/expenses", label: "Expenses", icon: Receipt, roles: ADMIN, badge: "expenses" },
      { to: "/in-house/pos", label: "Purchase Orders", icon: Receipt, roles: ADMIN, badge: "pos" },
    ],
  },
  {
    id: "hr", label: "HR", icon: IdentificationBadge, collapsible: true, roles: ADMIN,
    items: [
      { to: "/hr", label: "My HR", icon: IdentificationBadge, roles: ADMIN, exact: true },
      { to: "/hr/team", label: "HR Hub", icon: UsersThree, roles: ADMIN, opsDirectorOnly: true },
    ],
  },
  {
    id: "suppliers", label: "Suppliers", icon: Truck, collapsible: true, roles: [...ADMIN, "team_member"],
    items: [
      { to: "/vendors", label: "Suppliers", icon: Truck, roles: ADMIN },
      { to: "/suppliers/nearby", label: "Nearby", icon: MapPin, roles: ADMIN },
      { to: "/supplier-compliance", label: "Compliance", icon: ShieldCheck, roles: ADMIN },
      { to: "/supplier-analytics", label: "Analytics", icon: ChartBar, roles: ADMIN },
      { to: "/supplier-compare", label: "Compare", icon: ArrowsLeftRight, roles: ADMIN },
      { to: "/supplier-onboarding", label: "Onboarding", icon: Lightning, roles: ["super_admin", "admin"] },
      { to: "/supplier-outreach", label: "Outreach", icon: Megaphone, roles: ["super_admin", "admin"] },
      { to: "/vendor-scorecard", label: "Scorecard", icon: Trophy, roles: ADMIN },
      { to: "/operative-scorecard", label: "Team Scorecard", icon: Trophy, roles: [...ADMIN, "team_member"] },
    ],
  },
  {
    id: "cmms", label: "CMMS", icon: Wrench, collapsible: true, roles: ADMIN,
    items: [
      { to: "/predictive-maintenance", label: "Predictive Maintenance", icon: Sparkle, roles: ADMIN },
    ],
  },
  {
    id: "health_safety", label: "Health & Safety", icon: HardHat, collapsible: true, roles: ADMIN,
    items: [
      { to: "/health-safety?tab=overview", label: "H&S Overview", icon: SquaresFour, roles: ADMIN },
      { to: "/health-safety?tab=documents", label: "HSE Documents", icon: Books, roles: ADMIN },
      { to: "/health-safety?tab=coshh", label: "COSHH", icon: Flask, roles: ADMIN },
      { to: "/health-safety?tab=inspections", label: "Inspections", icon: ListChecks, roles: ADMIN },
      { to: "/health-safety?tab=incidents", label: "Incidents", icon: FirstAid, roles: ADMIN },
      { to: "/health-safety?tab=rams", label: "RAMS", icon: HardHat, roles: ADMIN },
      { to: "/health-safety?tab=dra", label: "Risk Assessments", icon: WarningDiamond, roles: ADMIN },
      { to: "/health-safety?tab=permits", label: "Permits to Work", icon: SealCheck, roles: ADMIN },
      { to: "/health-safety?tab=talks", label: "Toolbox Talks", icon: ChalkboardTeacher, roles: ADMIN },
      { to: "/lone-worker", label: "Lone Worker", icon: Siren, roles: ADMIN },
      { to: "/health-safety?tab=training", label: "Training", icon: GraduationCap, roles: ADMIN },
      { to: "/health-safety?tab=competency", label: "Competency Matrix", icon: Certificate, roles: ADMIN },
    ],
  },
  {
    id: "support", label: "Support", icon: Lifebuoy, collapsible: true,
    items: [
      { to: "/ai-assistant", label: "AI Assistant", icon: Robot, roles: ADMIN },
      { to: "/knowledge-base", label: "Knowledge Base", icon: Book, roles: [...ADMIN, "team_member"] },
    ],
  },
  {
    id: "operational", label: "Operational Info", icon: ChartPieSlice, collapsible: true,
    items: [
      { to: "/portfolio-health", label: "Portfolio Health", icon: Heartbeat, roles: [...ADMIN, "stakeholder"] },
      { to: "/slas", label: "SLAs", icon: Gauge, roles: ADMIN },
      { to: "/compliance-hub", label: "Compliance", icon: ShieldCheck, roles: [...ADMIN, "in_house"] },
      { to: "/document-expiry", label: "Compliance Calendar", icon: CalendarBlank, roles: ADMIN },
      { to: "/activity-log", label: "Activity Log", icon: Lightning, roles: ADMIN },
      { to: "/reports", label: "Reports", icon: ChartBar, roles: ADMIN, exact: true },
      { to: "/staff-performance", label: "Staff Performance", icon: ChartLineUp, roles: ["super_admin"] },
      { to: "/staff-monitoring", label: "Staff Monitoring", icon: Monitor, roles: ADMIN, opsDirectorOnly: true },
    ],
  },
  {
    id: "revenue", label: "Revenue & Growth", icon: Coins, collapsible: true, roles: ["super_admin", "admin"],
    items: [
      { to: "/platform/referrals", label: "Referral Programme", icon: Megaphone, roles: ["super_admin", "admin"] },
      { to: "/platform/usage-alerts", label: "Usage Alerts", icon: Gauge, roles: ["super_admin", "admin"] },
      { to: "/platform/downgrade", label: "Manage Subscription", icon: HandPalm, roles: ["super_admin", "admin"] },
      { to: "/platform/cohort-analysis", label: "Cohort Analysis", icon: ChartLineUp, roles: ["super_admin"], platformAdminOnly: true },
    ],
  },
  {
    id: "insights", label: "Insights", icon: TrendUp, collapsible: true, roles: ADMIN,
    items: [
      { to: "/tenant-analytics", label: "Analytics", icon: ChartBar, roles: ADMIN },
      { to: "/reports/profit-loss", label: "Profit & Loss", icon: TrendUp, roles: ["super_admin", "admin"] },
      { to: "/reports/supplier-scorecard", label: "Supplier Scorecard", icon: Star, roles: ADMIN },
      { to: "/reports/client-spend", label: "Client Spend", icon: UsersThree, roles: ADMIN },
      { to: "/marked-up-quotes", label: "Marked-Up Quotes", icon: ChartLineUp, roles: ADMIN, opsDirectorOnly: true },
    ],
  },
  {
    id: "settings", label: "Settings", icon: Gear, collapsible: true, roles: ADMIN,
    items: [
      { to: "/job-forms", label: "Job Forms", icon: PenNib, roles: ADMIN },
      { to: "/settings/checklists", label: "Checklists", icon: PenNib, roles: ADMIN },
      { to: "/templates", label: "Templates", icon: Bookmark, roles: ADMIN },
      { to: "/document-templates", label: "Doc Templates", icon: FileText, roles: ADMIN },
    ],
  },
  {
    id: "vendor_portal",
    items: [{ to: "/vendors", label: "Documents", icon: ShieldCheck, roles: ["vendor"] }],
  },
  {
    id: "in_house_portal", label: "My Portal", roles: ["in_house", "vendor_operative"],
    items: [
      { to: "/my-work", label: "My Work", icon: Briefcase, roles: ["in_house", "vendor_operative"] },
      { to: "/my-work#schedule", label: "Schedule", icon: CalendarBlank, roles: ["in_house", "vendor_operative"] },
      { to: "/my-work/route-planner", label: "Route Planner", icon: MapTrifold, roles: ["in_house", "vendor_operative"] },
      { to: "/compliance-hub", label: "Compliance", icon: ShieldCheck, roles: ["in_house"] },
      { to: "/messages", label: "Messages", icon: ChatCircle, roles: ["in_house"], badge: "messages" },
      { to: "/whatsapp", label: "WhatsApp", icon: WhatsappLogo, roles: ["in_house"] },
      { to: "/notifications", label: "Alerts", icon: Bell, roles: ["in_house", "vendor_operative"], badge: "notifications" },
      { to: "/my-work/help", label: "Help", icon: Book, roles: ["in_house", "vendor_operative"] },
    ],
  },
];

/** Users may star up to this many menu items into the Favourites row. */
export const MAX_FAVOURITES = 5;

/** Links plus their nested children, in display order. */
export const flattenItems = (items) => items.flatMap((i) => [i, ...(i.children || [])]);

/** Role / flag / permission filter shared by the sidebar and the mobile drawer. */
export function visibleItemsFor(section, user, { isMainClient = false, sectionAllowed = () => true } = {}) {
  if (section.roles && !section.roles.some((r) => r === user?.role)) return [];
  if (section.platformAdminOnly && !user?.is_platform_admin) return [];
  const ok = (item) => item.roles.includes(user?.role) && (!item.platformAdminOnly || user?.is_platform_admin)
    && (!item.opsDirectorOnly || user?.operations_director) && (!item.mainClientOnly || isMainClient)
    && sectionAllowed(item.to.split("#")[0].split("?")[0]);
  return section.items.filter(ok).map((item) => (item.children ? { ...item, children: item.children.filter(ok) } : item));
}

/** True when `item` (or one of its children) is the current page. Mirrors the sidebar highlight rules. */
export function itemIsActive(item, location) {
  if (item.activeWhen) return item.activeWhen(location);
  const [itemPathQ, itemHash] = item.to.split("#");
  const [itemPath, itemQuery] = itemPathQ.split("?");
  const currentHash = location.hash.replace("#", "");
  // Query-string links (e.g. /health-safety?tab=permits) are active only for their tab; the first tab also covers nested routes.
  const queryMatch = !itemQuery || (() => {
    const want = new URLSearchParams(itemQuery);
    const have = new URLSearchParams(location.search);
    const nested = location.pathname.match(/^\/health-safety\/(docs|inspections|incidents|rams|coshh)\//);
    const nestedTab = nested ? ({ docs: "documents" }[nested[1]] || nested[1]) : "overview";
    return [...want.entries()].every(([k, v]) => (have.get(k) || (k === "tab" ? nestedTab : "")) === v);
  })();
  if (item.exact) return location.pathname === itemPath;
  if (itemHash) return location.pathname === itemPath && currentHash === itemHash;
  if (itemPath === "/") return location.pathname === "/" && !location.hash;
  return location.pathname.startsWith(itemPath) && queryMatch && !(currentHash && itemPath === location.pathname);
}
